/**
 * Kent Traders Back-in-Stock Notification System
 * Handles form submission and Firebase function integration
 */

// Prevent duplicate class declaration
if (typeof window.KTBackInStock === 'undefined') {
  window.KTBackInStock = class KTBackInStock {
    constructor() {
        this.init();
    }

    init() {
        this.setupFormHandlers();
        this.setupImpressionTracking();
    }

    setupImpressionTracking() {
        const widgets = document.querySelectorAll('.kt-back-in-stock');
        const recordImpression = (widget) => {
            if (widget.dataset.ktImpressionTracked === 'true') return;
            widget.dataset.ktImpressionTracked = 'true';
            this.trackJourney('back_in_stock_impression', {
                product_id: widget.dataset.productId || '',
                variant_id: widget.dataset.variantId || ''
            });
        };

        if (!('IntersectionObserver' in window)) {
            widgets.forEach(recordImpression);
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                recordImpression(entry.target);
                observer.unobserve(entry.target);
            });
        }, { threshold: 0.25 });
        widgets.forEach(widget => observer.observe(widget));
    }

    setupFormHandlers() {
        // Handle all back-in-stock forms
        const forms = document.querySelectorAll('[data-back-in-stock-form]');
        
        forms.forEach(form => {
            form.addEventListener('submit', (e) => {
                e.preventDefault();
                this.handleFormSubmission(form);
            });
        });
    }

    async handleFormSubmission(form) {
        const submitButton = form.querySelector('button[type="submit"]');
        const originalText = submitButton.textContent;
        
        try {
            // Show loading state
            this.showLoading(submitButton, 'Submitting...');
            
            // Get form data
            const formData = new FormData(form);
            const data = {
                productId: formData.get('product_id') || this.getProductIdFromUrl(),
                variantId: formData.get('variant_id') || null,
                productName: formData.get('product_title') || this.getProductTitle(),
                email: formData.get('email'),
                customerName: formData.get('name') || '',
                desiredQuantity: formData.get('desired_quantity') || '1',
                productPrice: this.getProductPrice(),
                productUrl: window.location.href,
                shop: window.Shopify?.shop || window.location.hostname
            };

            // Validate required fields
            if (!data.productId || !data.email) {
                throw new Error('Product ID and email are required');
            }

            // Submit to Firebase function
            const response = await fetch('https://us-central1-kt-shopify-apps.cloudfunctions.net/subscribeToStockNotificationHTTP', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const errorMessage = typeof errorData.error === 'string'
                    ? errorData.error
                    : errorData.error?.message;
                throw new Error(errorMessage || 'Failed to create subscription');
            }

            const result = await response.json();

            this.trackJourney('back_in_stock_request', {
                product_id: String(data.productId),
                variant_id: data.variantId ? String(data.variantId) : '',
                desired_quantity: Number(data.desiredQuantity) || 1
            });
            
            // Show success message
            this.showSuccessMessage(form, result);
            
        } catch (error) {
            console.error('Back-in-stock subscription error:', error);
            this.showErrorMessage(form, error.message);
        } finally {
            // Reset button
            this.hideLoading(submitButton, originalText);
        }
    }

    trackJourney(eventName, data) {
        if (window.KTJourneyTracker && typeof window.KTJourneyTracker.track === 'function') {
            window.KTJourneyTracker.track(eventName, data);
        }
    }

    showLoading(button, text) {
        button.disabled = true;
        button.textContent = text;
        button.classList.add('loading');
    }

    hideLoading(button, originalText) {
        button.disabled = false;
        button.textContent = originalText;
        button.classList.remove('loading');
    }

    showSuccessMessage(form, result) {
        // Hide form
        form.style.display = 'none';
        
        // The status panels are siblings of the form wrapper, inside the widget.
        const container = form.closest('.kt-back-in-stock') || form.parentElement;
        const successMessage = container.querySelector('[data-kt-success-message]');
        
        if (successMessage) {
            successMessage.hidden = false;
            successMessage.style.display = 'block';
            
            // Update message with product name
            const messageElement = successMessage.querySelector('.kt-success-message');
            if (messageElement) {
                const productName = this.getProductTitle();
                const template = container.dataset?.successTemplate || `We'll notify you as soon as __PRODUCT__ is back in stock.`;
                messageElement.textContent = template.replace('__PRODUCT__', productName);
            }
        } else {
            // Fallback: show alert if no success message element found
            alert('✅ Notification set! We\'ll notify you when this product is back in stock.');
        }
    }

    showErrorMessage(form, errorMessage) {
        // The status panels are siblings of the form wrapper, inside the widget.
        const container = form.closest('.kt-back-in-stock') || form.parentElement;
        const errorElement = container.querySelector('[data-kt-error-message]');
        
        if (errorElement) {
            errorElement.hidden = false;
            errorElement.style.display = 'block';
            
            // Update error message
            const messageElement = errorElement.querySelector('.kt-error-message');
            if (messageElement) {
                messageElement.textContent = errorMessage;
            }
        } else {
            // Fallback: show alert if no error message element found
            alert('❌ Error: ' + errorMessage);
        }
    }





    // Helper methods
    getProductIdFromUrl() {
        const path = window.location.pathname;
        const match = path.match(/\/products\/([^\/]+)/);
        return match ? match[1] : null;
    }


    getProductTitle() {
        const titleElement = document.querySelector('h1, .product-title, [data-product-title]');
        return titleElement ? titleElement.textContent.trim() : 'this product';
    }

    getProductPrice() {
        const priceElement = document.querySelector('.price, .product-price, [data-product-price]');
        if (priceElement) {
            const priceText = priceElement.textContent.trim();
            const match = priceText.match(/£?(\d+\.?\d*)/);
            return match ? match[1] : '0.00';
        }
        return '0.00';
    }
  };
}

// Initialize only once
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        if (!window.__ktBackInStockInitialized) {
            window.__ktBackInStockInitialized = true;
            new window.KTBackInStock();
        }
    });
} else {
    if (!window.__ktBackInStockInitialized) {
        window.__ktBackInStockInitialized = true;
        new window.KTBackInStock();
    }
}
